import { toLegacy } from "./prettyUrls";
// Per-route SEO: unique <title>, meta description and Open Graph / Twitter tags.
// Public PWHL pages are listed in PUBLIC_ROUTES (also used to build sitemap.xml);
// private / admin / NHL pages get robots "noindex".
const SITE = "Rosterly";
const DEFAULT_DESC = "Rosterly — PWHL stats, rosters, lineups, standings, schedule and league history for every team and player.";

export const PUBLIC_ROUTES = {
    "/": ["PWHL Stats, Rosters & Lineups", DEFAULT_DESC],
    "/overview": ["PWHL Overview", "A snapshot of the PWHL: standings, leaders, recent games and the latest moves across the league."],
    "/teams": ["PWHL Teams", "Every PWHL team — rosters, staff, records, contracts and history in one place."],
    "/team-pages": ["PWHL Team Pages", "Browse every PWHL club and dive into its roster, staff, records and history."],
    "/standings": ["PWHL Standings", "Live PWHL standings with points, records, goal differential and playoff picture."],
    "/schedule": ["PWHL Schedule & Results", "The full PWHL schedule with scores, results and upcoming games for every team."],
    "/leaderboards": ["PWHL Leaderboards", "PWHL player leaderboards — goals, assists, points, rates, splits and goalie stats by season."],
    "/team-leaderboards": ["PWHL Leaderboards", "PWHL stat leaders — goals, assists, points, rates, splits and goalie stats by season."],
    "/rosters": ["PWHL Rosters", "Current PWHL rosters for every team, with positions, numbers and player profiles."],
    "/active-lineup": ["PWHL Lineups", "Projected and recent PWHL lineups — forward lines, defence pairs and goalies for every team."],
    "/contracts": ["PWHL Contracts", "PWHL contract history — signings, terms and re-signings for every player and team."],
    "/team-draft": ["PWHL Draft Picks by Team", "Every PWHL draft pick, organised by team and round."],
    "/draft": ["PWHL Draft", "PWHL draft history — every pick, round and selection since the league began."],
    "/team-movements": ["PWHL History & Transactions", "Day-by-day PWHL history: signings, trades, draft and expansion picks, injuries, discipline and staff moves."],
    "/transfers": ["PWHL History & Transactions", "Day-by-day PWHL history: signings, trades, draft and expansion picks, injuries, discipline and staff moves."],
    "/trades": ["PWHL Trades", "Every PWHL trade with the players, picks and teams involved."],
    "/injuries": ["PWHL Injuries", "PWHL injury log — placements, activations and games missed for every player."],
    "/discipline": ["PWHL Discipline", "PWHL suspensions and fines — offenses, incident dates and games served."],
    "/staff": ["PWHL Coaches & Staff", "Current PWHL general managers, head coaches and staff for every team, with tenures."],
    "/staff-moves": ["PWHL Staff Moves", "PWHL coaching and front-office hires, promotions and departures."],
    "/team-records": ["PWHL Team Records", "PWHL team records — single-season and all-time bests for every club."],
    "/team-history": ["PWHL Team History", "Season-by-season history of every PWHL team."],
    "/records": ["PWHL Records", "PWHL player records — career, single-season and single-game bests."],
    "/milestones": ["PWHL Milestones", "Career milestones reached and approaching across the PWHL."],
    "/compare": ["Compare PWHL Players", "Compare PWHL players side by side across every stat and season."],
    "/chemistry": ["PWHL Chemistry", "PWHL line and pair chemistry — which teammates produce together."],
    "/trends": ["PWHL Trends", "Hot and cold streaks and recent form for PWHL players and teams."],
    "/insights": ["PWHL Insights", "Deeper PWHL analysis and stat insights."],
    "/arenas": ["PWHL Arenas", "Every PWHL arena with location and team history."],
    "/championships": ["PWHL Champions", "Every PWHL Walter Cup champion and playoff result."],
    "/awards": ["PWHL Awards", "PWHL award winners and finalists by season."],
    "/rankings": ["PWHL Player Rankings", "Fan-voted PWHL player rankings."],
    "/stories": ["PWHL Stories", "Stories and features from across the PWHL."],
    "/news": ["PWHL News", "The latest PWHL news, signings and announcements."],
    "/socials/news": ["PWHL News", "The latest PWHL news, signings and announcements."],
    "/videos": ["PWHL Videos", "PWHL highlights and videos."],
    "/olympics": ["Olympic Women's Hockey", "PWHL players at the Olympics — rosters and results."],
    "/ncaa": ["NCAA Women's Hockey", "NCAA women's hockey prospects heading to the PWHL."],
    "/frozen-four": ["Women's Frozen Four", "The NCAA Women's Frozen Four and future PWHL talent."],
    "/demographics": ["PWHL Demographics", "Where PWHL players come from — nationalities, ages and backgrounds."],
    "/fan-wall": ["PWHL Fan Wall", "Fan posts and creations from the Rosterly community."],
    "/contact": ["Contact", "Get in touch with the Rosterly team."],
    "/terms": ["Terms of Use", "Rosterly terms of use."],
    "/privacy": ["Privacy Policy", "Rosterly privacy policy."],
    "/legal": ["Legal", "Rosterly Terms & Conditions, Privacy Policy and Cookies."],
    "/nhl/schedule": ["NHL Schedule & Scores", "The NHL schedule with live scores, results, lineups and Game Centre for every game."],
    "/nhl/standings": ["NHL Standings", "Live NHL standings by division, conference and league with points and records."],
    "/nhl/leaderboards": ["NHL Leaderboards", "NHL player leaderboards — goals, assists, points and goalie stats."],
    "/nhl/rosters": ["NHL Rosters", "Current NHL rosters for every team."],
    "/nhl/lineups": ["NHL Lineups", "Projected and confirmed NHL lineups — lines, pairs and goalies for every team."],
    "/nat/schedule": ["Women's World Championship Schedule", "The IIHF Women's World Championship schedule with scores, groups and results."],
    "/nat/standings": ["Women's World Championship Standings", "IIHF Women's World Championship group standings."],
    "/all/schedule": ["Hockey Schedule — Every League", "PWHL, NHL, NCAA and WWHC hockey games on one schedule with scores and results."],
};

const DYNAMIC = [
    [/^\/players\/[^/]+/, "PWHL Player Profile", "PWHL player profile — stats, game logs, contracts and career history."],
    [/^\/teams\/[^/]+/, "PWHL Team", "PWHL team page — roster, staff, stats and history."],
    [/^\/coaches\/[^/]+/, "PWHL Coach Profile", "PWHL coach and staff profile with career tenure."],
    [/^\/leagues\/[^/]+/, "League", "League overview on Rosterly."],
];

const PRIVATE = /^\/(admin|account|tasks|changelog|roadmap|bug-reports|studio|template-studio|creator|messages|packs|switchboard|stats-switchboard|archive|gallery)(\/|$)/;

const setMeta = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute("content", content);
};
const setCanonical = (href) => {
    let el = document.head.querySelector('link[rel="canonical"]');
    if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
    el.setAttribute("href", href);
};

// Keeps og/twitter titles in step when a page refines the tab title (player / team / game names).
export const setSeoTitle = (title) => { setMeta("property", "og:title", title); setMeta("name", "twitter:title", title); };

// Returns the SEO page name for a route (null → caller keeps its own title).
export function applySeo(pathname) {
    const path = pathname.replace(/\/+$/, "") || "/";
    const hit = PUBLIC_ROUTES[path] || (DYNAMIC.find(([rx]) => rx.test(path)) || []).slice(1);
    const [name, desc] = hit && hit.length ? hit : [null, DEFAULT_DESC];
    const title = name ? `${SITE} - ${name}` : document.title;
    const sp = new URLSearchParams(window.location.search);
    const gameQ = sp.get("game") ? `?game=${encodeURIComponent(sp.get("game"))}${sp.get("matchup") ? `&matchup=${encodeURIComponent(sp.get("matchup"))}` : ""}` : "";
    const url = `${window.location.origin}${window.location.pathname.replace(/\/+$/, "") || "/"}${gameQ}`;
    setMeta("name", "description", desc);
    setMeta("name", "robots", PRIVATE.test(path) ? "noindex, nofollow" : "index, follow");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setCanonical(url);
    return name;
}

// Game links (SEO): every schedule game has a crawlable URL — ?game=ID&matchup=away-vs-home on its schedule page.
const slugify = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
export const gameSlug = (away, home) => `${slugify(away)}-vs-${slugify(home)}`;
export const gameHref = (path, id, away, home) => `${path}?game=${encodeURIComponent(id)}&matchup=${gameSlug(away, home)}`;
// Middle-click a schedule card → open that game in a new in-app tab (browser tab only when in-app tabs are off).
export const auxHandlers = (href) => ({
    auxOpen: (e) => {
        if (e.button !== 1) return;
        e.preventDefault(); e.stopPropagation();
        if (window.__rosterlyTabs) { const [p, q = ""] = href.split("?"); window.dispatchEvent(new CustomEvent("rosterly:open-tab", { detail: { path: `${toLegacy(p)}${q ? `?${q}` : ""}` } })); }
        else window.open(href, "_blank", "noopener");
    },
    auxDown: (e) => { if (e.button === 1) e.preventDefault(); },
});

// Opening a game on a schedule rewrites the address bar (no page change); closing it restores the schedule URL.
let baseTitle = null;
export function syncGameUrl(id, info = {}) {
    const u = new URL(window.location.href);
    if (id == null) { u.searchParams.delete("game"); u.searchParams.delete("matchup"); }
    else { u.searchParams.set("game", String(id)); if (info.away && info.home) u.searchParams.set("matchup", gameSlug(info.away, info.home)); }
    const next = `${u.pathname}${u.search}${u.hash}`;
    if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) window.history.replaceState(window.history.state, "", next);
    if (id != null && info.away && info.home) {
        if (baseTitle == null) baseTitle = document.title;
        const t = `${info.away} vs ${info.home}${info.league ? ` · ${info.league}` : ""} · ${SITE}`;
        document.title = t;
        setSeoTitle(t);
        setMeta("name", "description", `${info.away} vs ${info.home}${info.date ? ` on ${info.date}` : ""} — live score, lineups, stats and play-by-play on ${SITE}.`);
        setCanonical(`${u.origin}${next}`);
    } else if (id == null && baseTitle != null) {
        document.title = baseTitle; setSeoTitle(baseTitle); baseTitle = null;
        setCanonical(`${u.origin}${u.pathname}`);
    }
}
